import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as services from "../service/MyTimesheetService";
import { useDispatch, useSelector } from "react-redux";
import { setIsLoading, setMyTimesheetById, setTimesheetByDay, setTimesheetCalendar } from "../slices/myTimesheetSlice";
import { Flex, message } from "antd";
import dayjs from "dayjs";
import { generateRandomString } from "utils/helper";
import styles from "../styles/MyTimesheet.module.css";
import MyTimesheetDetailModal from "../components/MyTimesheetDetailModal";
import MyTimesheetUpdateModal from "../components/MyTimesheetUpdateModal";

const useMyTimesheet = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const [columns, setColumns] = useState([]);
    const { currentCompany, currentEmployee } = useSelector((state) => state.company);
    const { timesheetCalendar, timesheetByDay, myTimesheetById } = useSelector((state) => state.myTimesheet);

    const [startDate, setStartDate] = useState(dayjs().startOf('month').format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(dayjs().endOf('month').format("YYYY-MM-DD"));
    const [currentDate, setCurrentDate] = useState(dayjs());

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
    const [isUpdateMode, setIsUpdateMode] = useState(false);
    const [selectedCell, setSelectedCell] = useState(null);

    const [timesheetOptions, setTimesheetOptions] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [taskOptions, setTaskOptions] = useState([]);
    const [selectedTimeSheetId, setSelectedTimeSheetId] = useState(null);
    const [selectedRoleId, setSelectedRoleId] = useState(null);
    const [pagination, setPagination] = useState({ page: 0, size: 10 });

    const modifiedTimesheetOption = useMemo(() => {
        const modifiedOptions = timesheetOptions?.map((opt) => ({
            ...opt,
            value: opt?.stpTimeSheetId,
            label: i18n.language === "en" ? opt?.projectNameEn : opt?.projectName,
        }));

        return modifiedOptions;
    }, [timesheetOptions, i18n.language]);

    const modifiedRoleOption = useMemo(() => {
        const modifiedOptions = roleOptions?.map((opt) => ({
            ...opt,
            value: opt?.stpTimeSheetRoleId,
            label: i18n.language === "en" ? opt?.roleNameEn : opt?.roleName,
        }));

        return modifiedOptions;
    }, [roleOptions, i18n.language]);

    const buildTreeFromRawData = (data, lang = "th") => {
        if (!Array.isArray(data)) return [];

        const flatList = [];

        data.forEach(task => {
            const hasChildren = Array.isArray(task.children) && task.children.length > 0;

            flatList.push({
                id: task.id,
                name: task.taskName,
                nameEn: task.taskNameEn,
                label: lang === "en" ? task.taskNameEn : task.taskName,
                value: task.id,
                disabled: hasChildren,
            });

            if (hasChildren) {
                task.children.forEach(subTask => {
                    flatList.push({
                        id: subTask.id,
                        name: subTask.taskName,
                        nameEn: subTask.taskNameEn,
                        label: lang === "en" ? subTask.taskNameEn : subTask.taskName,
                        value: subTask.id,
                        disabled: false,
                        under: { id: task.id },
                    });
                });
            }
        });

        return flatList;
    };

    const modifiedTaskOption = useMemo(() => {
        return buildTreeFromRawData(taskOptions, i18n.language);
    }, [taskOptions, i18n.language]);


    const getTimesheetByEmpIdOptions = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee
                };
                const response = await services.dropdownProjectByEmpId(values);
                setTimesheetOptions(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    const getRoleByEmpIdOptions = useCallback(async (timeSheetId) => {
        if (typeof currentCompany !== "object" && timeSheetId) {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    stpTimeSheetId: timeSheetId
                };
                const response = await services.dropdownRoleByEmpIdAndStpTimeSheetId(values);
                setRoleOptions(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    const getTaskByEmpIdOptions = useCallback(async (timeSheetId, roleId) => {
        if (typeof currentCompany !== "object" && timeSheetId) {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployee,
                    stpTimeSheetId: timeSheetId,
                    roleId: roleId
                };
                const response = await services.dropdownTaskByRole(values);
                setTaskOptions(response?.data?.content);
            } catch (error) {
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployee, dispatch]);

    useEffect(() => {
        if (currentCompany && isUpdateModalOpen) {
            getTimesheetByEmpIdOptions()
        }
    }, [currentCompany, getTimesheetByEmpIdOptions, isUpdateModalOpen])

    useEffect(() => {
        if (selectedTimeSheetId) {
            getRoleByEmpIdOptions(selectedTimeSheetId);
        }
    }, [selectedTimeSheetId, getRoleByEmpIdOptions]);

    useEffect(() => {
        console.log("selectedTimeSheetId && selectedRoleId", selectedTimeSheetId, selectedRoleId);

        if (selectedTimeSheetId && selectedRoleId) {
            getTaskByEmpIdOptions(selectedTimeSheetId, selectedRoleId);
        }
    }, [selectedTimeSheetId, getRoleByEmpIdOptions, selectedRoleId, getTaskByEmpIdOptions]);

    const getMyTimesheetByIdData = useCallback(async (params) => {
        try {
            dispatch(setIsLoading(true))

            const response = await services.getMyTimesheetById({
                employeeId: currentEmployee,
                companyId: currentCompany,
                txnMyTimeSheetId: params.txnMyTimeSheetId
            })

            dispatch(setMyTimesheetById(response?.data?.content))
        } catch (error) {
            message.error(t("common.error"))
        } finally {
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, currentEmployee, dispatch])

    const getTimesheetListByDay = useCallback(async (params, selectedDate) => {
        try {
            dispatch(setIsLoading(true))

            const response = await services.getTimesheetListByDay({
                ...params,
                employeeId: currentEmployee,
                companyId: currentCompany,
                date: selectedDate,
            })

            dispatch(setTimesheetByDay(response?.data?.page?.content))
            setPagination({
                ...params?.pagination,
                current: response?.data?.page?.number + 1,
                pageNo: response?.data?.page?.number + 1,
                page: response?.data?.page?.number + 1,
                total: response.data?.page?.totalElements,
                size: response?.data?.page?.totalPages
            });
        } catch (error) {
            message.error(t("common.error"))
        } finally {
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, currentEmployee, dispatch])

    const generateColumns = useCallback((selectedDate) => {
        const momentDate = dayjs(selectedDate);
        const startOfMonth = momentDate.clone().startOf('month');
        const endOfMonth = momentDate.clone().endOf('month');
        const daysInMonth = endOfMonth.date();

        const baseColumns = [
            {
                title: <Flex justify="center" style={{ color: "#42526D", fontWeight: "normal", fontSize: "20px" }}>
                    {t("my_timesheet.label.date")}
                </Flex>,
                dataIndex: 'project',
                key: 'project',
                fixed: 'left',
                width: 300,
                render: (text, record) => {
                    if (record.type === 'summaryDay') {
                        return (
                            <div style={{ color: '#42526D', }}>
                                {t("my_timesheet.label.total_working_hour")}
                            </div>
                        );
                    }

                    const indent = {
                        project: 0,
                        role: 12,
                        task: 24,
                        subtask: 40,
                    }[record.type] || 0;

                    return (
                        <div style={{
                            paddingLeft: indent,
                            fontWeight: record.type === 'project' ? 'bold' : 'normal',
                            color: "#42526D"
                        }}>
                            {record.name}
                        </div>
                    );
                }
            }
        ];

        const dayColumns = [];
        for (let day = 1; day <= daysInMonth; day++) {
            const currentDay = startOfMonth.clone().date(day);
            const dayOfWeek = currentDay.format('dd');
            const isWeekend = currentDay.day() === 0 || currentDay.day() === 6;
            const isToday = currentDay.isSame(moment(), 'day');
            const dateKey = currentDay.format('YYYY-MM-DD');

            dayColumns.push({
                title: (
                    <div style={{
                        textAlign: 'center',
                        backgroundColor: isToday ? "#2CADE7" : "transparent",
                        borderRadius: "4px",
                        padding: "4px"
                    }}>
                        <div style={{
                            fontWeight: 'bold', color: isToday ? "white" : "black"
                        }}>{day}</div>
                        <div style={{
                            fontSize: '12px',
                            color: isToday ? "white" : isWeekend ? '#ff4d4f' : '#9D9CA3',
                        }}>{dayOfWeek}</div>
                    </div>
                ),
                dataIndex: ['timeLogs', dateKey],
                key: dateKey,
                width: 80,
                align: 'center',
                className: isWeekend ? styles.weekendColumn : '',
                render: (value, record) => {
                    const handleClick = () => {
                        const isLeafTask =
                            record.type === "subtask" ||
                            (record.type === "task" && (!record.children || record.children.length === 0));

                        const isSummaryDay = record.type === 'summaryDay';
                        const isProjectSummary = record.type === 'project';

                        const canClickInUpdateMode = isUpdateMode && isLeafTask;

                        const canClickInNormalMode =
                            !isUpdateMode && (isLeafTask || record.type === "subtask" || isSummaryDay || isProjectSummary);

                        if (!(canClickInUpdateMode || canClickInNormalMode)) return;
                        setSelectedRoleId(record.roleId)
                        setSelectedCell({
                            value: value?.render,
                            txnMyTimeSheetId: value?.value,
                            record: {
                                ...record,
                                rawData: timesheetCalendar,
                                projectId: record.projectId,
                                roleId: record.roleId,
                                taskId: record.taskId,
                                subtaskId: record.subtaskId,
                                type: record.type,
                                billable: record.billable,
                                reworkFlag: record.reworkFlag,
                            },
                            date: dateKey,
                        });
                    };

                    // return (
                    //     <div
                    //         style={{
                    //             // padding: '4px',
                    //             cursor: (() => {
                    //                 const isLeafTask =
                    //                     record.type === "subtask" ||
                    //                     (record.type === "task" && (!record.children || record.children.length === 0));

                    //                 const isSummaryDay = record.type === 'summaryDay';
                    //                 const isProjectSummary = record.type === 'project';

                    //                 const canClickInUpdateMode = isUpdateMode && isLeafTask;

                    //                 const canClickInNormalMode =
                    //                     !isUpdateMode && (isLeafTask || record.type === "subtask" || isSummaryDay || isProjectSummary);

                    //                 const isClickable = canClickInUpdateMode || canClickInNormalMode
                    //                 return (canClickInUpdateMode || canClickInNormalMode) ? 'pointer' : 'not-allowed';
                    //             })()
                    //         }}
                    //         onClick={handleClick}
                    //     >
                    //         {value?.render || ""}
                    //     </div>
                    // );
                    const isLeafTask =
                        record.type === "subtask" ||
                        (record.type === "task" && (!record.children || record.children.length === 0));
                    const isSummaryDay = record.type === 'summaryDay';
                    const isProjectSummary = record.type === 'project';
                    const canClickInUpdateMode = isUpdateMode && isLeafTask;
                    const canClickInNormalMode =
                        !isUpdateMode && (isLeafTask || record.type === "subtask" || isSummaryDay || isProjectSummary);

                    const isClickable = canClickInUpdateMode || canClickInNormalMode;

                    const displayValue = value?.render || "";
                    const shouldShowClickableEmptyCell = isUpdateMode && isLeafTask && !value?.render;

                    return (
                        <div
                            style={{
                                cursor: isClickable ? 'pointer' : 'not-allowed',
                                minHeight: '20px',
                                ...(shouldShowClickableEmptyCell)
                            }}
                            onClick={handleClick}
                        >
                            {displayValue}
                        </div>
                    );
                }
            });
        }
        setColumns([...baseColumns, ...dayColumns]);

        // return [...baseColumns, ...dayColumns];
    }, [isUpdateMode, t, timesheetCalendar]);

    useEffect(() => {
        if (selectedCell) {
            const isLeafTask =
                selectedCell.record?.type === "subtask" ||
                (selectedCell.record?.type === "task" &&
                    (!selectedCell.record?.children || selectedCell.record?.children.length === 0));

            const isSummaryDay = selectedCell.record?.type === 'summaryDay';

            const isProjectSummary = selectedCell.record?.type === 'project';

            const canClickInUpdateMode = isUpdateMode && isLeafTask;

            const canClickInNormalMode =
                !isUpdateMode &&
                (isLeafTask || selectedCell.record?.type === "subtask" || isSummaryDay || isProjectSummary);

            if (canClickInUpdateMode) {
                if (selectedCell?.txnMyTimeSheetId) {
                    getMyTimesheetByIdData(selectedCell);
                } else {
                    dispatch(setMyTimesheetById(null));
                }
                setIsUpdateModalOpen(true);
            } else if (canClickInNormalMode) {
                getTimesheetListByDay({ page: 0, size: 10 }, dayjs(selectedCell?.date).format("YYYY-MM-DD"));
                setIsModalOpen(true);
            }
        }
    }, [selectedCell]);

    const getTimesheetCalendarData = useCallback(async (startDateParam, endDateParam) => {
        try {
            dispatch(setIsLoading(true))

            const response = await services.getTimesheetCalendar({
                employeeId: currentEmployee,
                companyId: currentCompany,
                startDate: startDateParam,
                endDate: endDateParam,
            })
            dispatch(setTimesheetCalendar(response?.data?.content))
        } catch (error) {
            message.error(t("common.error"))
        } finally {
            dispatch(setIsLoading(false))
        }
    }, [currentCompany, currentEmployee, dispatch])

    const onDateChange = useCallback(
        (date) => {
            if (!date || !currentCompany || !currentEmployee) return;

            const selectedDate = dayjs(date);
            const newStartDate = selectedDate.startOf('month').format("YYYY-MM-DD");
            const newEndDate = selectedDate.endOf('month').format("YYYY-MM-DD");

            setCurrentDate(selectedDate);
            setStartDate(newStartDate);
            setEndDate(newEndDate);

            // const newColumns = generateColumns(selectedDate);
            // setColumns(newColumns);

            getTimesheetCalendarData(newStartDate, newEndDate);
        },
        [generateColumns, getTimesheetCalendarData, currentCompany, currentEmployee]
    );

    useEffect(() => {
        if (currentCompany && currentEmployee) {
            const selectedDate = dayjs();
            const newStartDate = selectedDate.startOf('month').format("YYYY-MM-DD");
            const newEndDate = selectedDate.endOf('month').format("YYYY-MM-DD");
            getTimesheetCalendarData(newStartDate, newEndDate)
        }
    }, [currentCompany, currentEmployee])

    useEffect(() => {
        if (currentCompany && currentEmployee) {
            generateColumns(dayjs());
        }
    }, [currentCompany, currentEmployee]);

    useEffect(() => {
        if (currentCompany && currentEmployee) {
            generateColumns(currentDate);
        }
    }, [currentCompany, currentEmployee, currentDate]);


    const getStatusColor = (status) => {
        switch (status) {
            case 'Draft':
                return '#A2ABBE';
            case 'Approved':
                return '#00BA34';
            case 'Pending':
                return '#EBD305';
            default:
                return '#A2ABBE';
        }
    };

    const formatWorkingTime = (hours, minutes, approveStatusEn, isSummary = false, isSummaryDay = false) => {
        const h = hours != null ? hours : 0;
        const m = minutes != null ? minutes : 0;

        const totalMinutes = h * 60 + m;
        let color;

        if (isSummaryDay) {
            color = totalMinutes >= 480 ? '#000000' : '#A2ABBE';
        } else if (isSummary) {
            color = '#A2ABBE';
        } else {
            color = getStatusColor(approveStatusEn);
        }

        return (
            <span
                style={{
                    fontWeight: 'bold',
                    cursor: "pointer",
                    color,
                    ...(isSummary
                        ? {}
                        : {
                            textDecorationLine: 'underline',
                        }),
                }}
            >
                {`${h}:${String(m).padStart(2, '0')}`}
            </span>
        );
    };


    const createSummaryRow = (data) => {
        const summaryTimeLogs = {};
        const summaryByProject = {};

        data.forEach((project) => {
            let projectTotalMinutes = 0;

            project.roleTaskMapping.forEach((mapping) => {
                mapping.task.forEach((task) => {
                    task.timeSheetList?.forEach((entry) => {
                        const date = entry.recordingDate;
                        const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);
                        // day
                        summaryTimeLogs[date] = (summaryTimeLogs[date] || 0) + totalMinutes;
                        // proj
                        projectTotalMinutes += totalMinutes;
                    });

                    task.children?.forEach((subtask) => {
                        subtask.timeSheetList?.forEach((entry) => {
                            const date = entry.recordingDate;
                            const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);
                            summaryTimeLogs[date] = (summaryTimeLogs[date] || 0) + totalMinutes;
                            projectTotalMinutes += totalMinutes;
                        });
                    });
                });
            });
            summaryByProject[project.projectName || project.projectCode || ""] = projectTotalMinutes;
        });

        const formattedTimeLogs = {};
        for (const [date, totalMinutes] of Object.entries(summaryTimeLogs)) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            formattedTimeLogs[date] = { render: formatWorkingTime(hours, minutes, '', false, true) };
        }

        const formattedProjectLogs = {};
        for (const [projectName, totalMinutes] of Object.entries(summaryByProject)) {
            const hours = Math.floor(totalMinutes / 60);
            const minutes = totalMinutes % 60;
            formattedProjectLogs[projectName] = formatWorkingTime(hours, minutes, '', true, false);
        }

        return [
            {
                key: 'summary-day-row',
                name: 'Total by Day',
                type: 'summaryDay',
                timeLogs: formattedTimeLogs,
            },
        ];
    };


    const transformTimesheetData = (data) => {
        const sortedData = [...data].sort((a, b) => {
            const nameA = (a.projectName || "").toLowerCase();
            const nameB = (b.projectName || "").toLowerCase();
            return nameA.localeCompare(nameB, 'th');
        });

        const result = sortedData?.map((project) => {
            const projectNode = {
                key: project.id,
                name: project.projectName,
                type: 'project',
                projectId: project.id,
                timeLogs: {},
                children: [],
            };

            const projectTotalLogs = {};

            const sortedRoleMappings = [...project.roleTaskMapping].sort((a, b) => {
                const nameA = (a.role?.roleName || "").toLowerCase();
                const nameB = (b.role?.roleName || "").toLowerCase();
                return nameA.localeCompare(nameB, 'th');
            });

            sortedRoleMappings.forEach((mapping, i) => {
                const roleNode = {
                    key: `${project.id}-role-${i}`,
                    name: mapping.role?.roleName || '-',
                    type: 'role',
                    projectId: project.id,
                    roleId: mapping.role?.id,
                    timeLogs: {},
                    children: [],
                };

                const sortedTasks = [...(mapping.task || [])].sort((a, b) => {
                    const nameA = (a.taskName || "").toLowerCase();
                    const nameB = (b.taskName || "").toLowerCase();
                    return nameA.localeCompare(nameB, 'th');
                });

                sortedTasks.forEach((task) => {
                    if (!task?.taskName && !task?.timeSheetList?.length && !task?.children?.length) {
                        return;
                    }

                    const taskTimeLogs = {};

                    task.timeSheetList?.forEach((entry) => {
                        const date = entry.recordingDate;
                        const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);

                        taskTimeLogs[date] = {
                            value: entry.myTimeSheetId,
                            render: formatWorkingTime(
                                entry?.totalWorkingHours,
                                entry?.totalWorkingMinutes,
                                entry?.approveStatusEn,
                            )
                        }
                        projectTotalLogs[date] = (projectTotalLogs[date] || 0) + totalMinutes;
                    });
                    const taskNode = {
                        key: `${project.id}-task-${task.id}`,
                        name: task.taskName,
                        type: 'task',
                        projectId: project.id,
                        roleId: mapping.role?.id,
                        taskId: task.id,
                        billable: task.billable,
                        reworkFlag: task.reworkFlag,
                        timeSheetList: task.timeSheetList,
                        timeLogs: taskTimeLogs,
                        children: [],
                    };

                    const sortedSubtasks = [...(task.children || [])].sort((a, b) => {
                        const nameA = (a.taskName || "").toLowerCase();
                        const nameB = (b.taskName || "").toLowerCase();
                        return nameA.localeCompare(nameB, 'th');
                    });

                    sortedSubtasks?.forEach((subtask) => {
                        const subtaskTimeLogs = {};

                        subtask.timeSheetList?.forEach((entry) => {

                            const date = entry.recordingDate;
                            const totalMinutes = (entry?.totalWorkingHours || 0) * 60 + (entry?.totalWorkingMinutes || 0);

                            subtaskTimeLogs[date] = {
                                value: entry.myTimeSheetId,
                                render: formatWorkingTime(
                                    entry?.totalWorkingHours,
                                    entry?.totalWorkingMinutes,
                                    entry?.approveStatusEn,
                                )
                            }

                            projectTotalLogs[date] = (projectTotalLogs[date] || 0) + totalMinutes;
                        });

                        const subtaskNode = {
                            key: `${project.id}-subtask-${subtask.id}`,
                            name: subtask.taskName,
                            type: 'subtask',
                            projectId: project.id,
                            roleId: mapping.role?.id,
                            taskId: task.id,
                            subtaskId: subtask.id,
                            billable: subtask.billable,
                            reworkFlag: subtask.reworkFlag,
                            timeLogs: subtaskTimeLogs,
                        };
                        taskNode.children.push(subtaskNode);
                    });

                    roleNode.children.push(taskNode);
                });

                projectNode.children.push(roleNode);
            });

            for (const [date, totalMinutes] of Object.entries(projectTotalLogs)) {
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;
                projectNode.timeLogs[date] = {
                    render: formatWorkingTime(hours, minutes, '', true)
                }
            }

            return projectNode;
        });

        const summaryRows = createSummaryRow(data);
        return [...summaryRows, ...result]
    };

    const onUpdateTimesheet = useCallback(async (params) => {
        dispatch(setIsLoading(true))
        const time = dayjs(params?.totalWorkingHours);
        const hours = time?.hour();
        const minutes = time?.minute();

        await services.updateMyTimesheet({
            ...params,
            companyId: currentCompany,
            employeeId: currentEmployee,
            startDate: dayjs(params?.recordingDate)?.format("YYYY-MM-DD"),
            totalWorkingHours: hours,
            totalWorkingMinutes: minutes,
            billableFlag: params?.billableFlag ? "Y" : "N",
            reworkFlag: params?.reworkFlag ? "Y" : "N",

        }).then((res) => {
            getTimesheetCalendarData(startDate, endDate)
        }).catch((error) => {
            message.error(t("common.error"))
        }).finally((o) => {
            message.success(t("common.success"))
            dispatch(setIsLoading(false));
        })
    }, [currentCompany, currentEmployee, dispatch, endDate, getTimesheetCalendarData, startDate])

    const modalDetail = useCallback(() => {
        return (
            <MyTimesheetDetailModal
                open={isModalOpen}
                setIsModalOpen={setIsModalOpen}
                selectedCell={selectedCell}
                timesheetByDay={timesheetByDay}
            />
        )
    }, [isModalOpen, selectedCell, timesheetByDay])

    const modalUpdateTimesheet = useCallback(() => {
        return (
            <MyTimesheetUpdateModal
                open={isUpdateModalOpen}
                setIsModalOpen={setIsUpdateModalOpen}
                selectedCell={selectedCell}
                roleOptions={modifiedRoleOption}
                taskOptions={modifiedTaskOption}
                timesheetOptions={modifiedTimesheetOption}
                setSelectedTimeSheetId={setSelectedTimeSheetId}
                setSelectedRoleId={setSelectedRoleId}
                onUpdateTimesheet={onUpdateTimesheet}
                myTimesheetById={myTimesheetById}
            />
        )
    }, [isUpdateModalOpen, onUpdateTimesheet, roleOptions, selectedCell, taskOptions, timesheetOptions, myTimesheetById])

    return {
        columns,
        timesheetCalendar,
        currentDate,
        isModalOpen,
        isUpdateMode,
        transformTimesheetData,
        onDateChange,
        setIsModalOpen,
        setIsUpdateMode,
        selectedCell,
        isUpdateModalOpen,
        setIsUpdateModalOpen,
        timesheetOptions: modifiedTimesheetOption,
        roleOptions: modifiedRoleOption,
        taskOptions: modifiedTaskOption,
        setSelectedTimeSheetId,
        setSelectedRoleId,
        onUpdateTimesheet,
        modalDetail,
        modalUpdateTimesheet,
        startDate,
        endDate,
        getTimesheetCalendarData
    }
}

export default useMyTimesheet;